// views/alert/AlertAdminEditView.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { updateAlertAdminApi, getAlertAdminApi } from '@/lib/api/alertAdmin';
import { useLoading } from '@/store/loadingStore';
import { useModal } from '@/store/modalStore';
import './AlertAdminForm.css';

const AlertAdminEditView = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { startLoading, stopLoading } = useLoading();
    const modal = useModal();

    // 탭 상태 추가
    const [activeTab, setActiveTab] = useState('경고알림 관리자');

    // 폼 데이터 상태 - 스크린샷에 있는 필드들만 유지
    const [formData, setFormData] = useState({
        name: '',
        contact: '',
        department: '',
        memo: '',
        regions: [] as string[]
    });

    // 관리자 정보 로드
    useEffect(() => {
        if (id) {
            loadAdminData(parseInt(id));
        }
    }, [id]);

    const loadAdminData = async (adminId: number) => {
        try {
            startLoading('관리자 정보 로드 중...');
            const data = await getAlertAdminApi(adminId);
            setFormData({
                name: data.name || '',
                contact: data.contact || '',
                department: data.department || '',
                memo: data.memo || '',
                regions: data.regions || []
            });
        } catch (error: any) {
            modal.alert('로드 실패', error.message || '관리자 정보를 불러오는 중 오류가 발생했습니다.');
            navigate('/alert/admin');
        } finally {
            stopLoading();
        }
    };

    // 탭 변경 핸들러
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        // 다른 탭 기능은 추후 구현
    };

    // 입력값 변경 핸들러
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 지역 체크박스 핸들러
    const handleRegionChange = (regionName: string, checked: boolean) => {
        setFormData(prev => {
            if (regionName === '지역전체') {
                // 지역전체 선택/해제 시 모든 지역 선택/해제
                const allRegions = [
                    '남평읍', '공산면', '금천면', '다시면', '다도면', '동강면', '문평면', '노안면', '봉황면', '반남면',
                    '산포면', '세지면', '왕곡면', '금남동', '빛가람동', '성북동', '송월동', '영강동', '영산동', '이창동'
                ];
                return {
                    ...prev,
                    regions: checked ? allRegions : []
                };
            } else {
                // 개별 지역 선택/해제
                const newRegions = checked
                    ? [...prev.regions, regionName]
                    : prev.regions.filter(region => region !== regionName);

                return {
                    ...prev,
                    regions: newRegions
                };
            }
        });
    };

    // 폼 제출
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!id) {
            modal.alert('오류', '관리자 ID가 없습니다.');
            return;
        }

        try {
            startLoading('관리자 수정 중...');
            await updateAlertAdminApi(parseInt(id), formData);
            modal.alert('수정 완료', '관리자 정보가 성공적으로 수정되었습니다.');
            navigate('/alert/admin');
        } catch (error: any) {
            modal.alert('수정 실패', error.message || '수정 중 오류가 발생했습니다.');
        } finally {
            stopLoading();
        }
    };

    // 취소
    const handleCancel = () => {
        modal.confirm(
            '수정 취소',
            '수정사항이 저장되지 않습니다. 취소하시겠습니까?',
            () => navigate('/alert/admin')
        );
    };

    // 지역 체크 상태 확인
    const allRegions = [
        '남평읍', '공산면', '금천면', '다시면', '다도면', '동강면', '문평면', '노안면', '봉황면', '반남면',
        '산포면', '세지면', '왕곡면', '금남동', '빛가람동', '성북동', '송월동', '영강동', '영산동', '이창동'
    ];
    const isAllSelected = allRegions.every(region => formData.regions.includes(region));

    return (
        <div className="device-list-container">
            {/* 탭 버튼 - StatView와 동일한 구조 */}
            <div className="tab-buttons">
                <div className="" style={{ float: "left" }}>
                    <button
                        className="tab-button"
                        onClick={() => {
                            window.location.href = "/alert";
                        }}
                    >
                        나주시 경고알림 현황
                    </button>
                    <button
                        className={`tab-button ${activeTab === '경고알림 관리자' ? 'active' : ''}`}
                        onClick={() => handleTabChange('경고알림 관리자')}
                    >
                        경고알림 관리자
                    </button>
                    <button
                        className="tab-button"
                        onClick={() => {
                            window.open("https://gabia.com", "_blank");
                        }}
                    >
                        문자충전
                    </button>
                </div>
            </div>

            {/* dlv_dv01 박스 - StatView와 동일한 구조 */}
            <div className="dlv_dv01">
                <div className="env-content">
                    {/* 헤더 */}
                    <div className="content-header">
                        <h1 className="page-title">담당자 수정</h1>
                    </div>

                    {/* 폼 영역 */}
                    <form onSubmit={handleSubmit}>
                        <table className="form-table">
                            <tbody>
                            {/* 이름 */}
                            <tr>
                                <td className="label-cell">이름</td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder=""
                                        required
                                    />
                                </td>
                            </tr>

                            {/* 연락처 */}
                            <tr>
                                <td className="label-cell">연락처</td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="contact"
                                        value={formData.contact}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder=""
                                        required
                                    />
                                </td>
                            </tr>

                            {/* 부서/맡은정보 */}
                            <tr>
                                <td className="label-cell">부서/맡은정보</td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder=""
                                    />
                                </td>
                            </tr>

                            {/* 관리지역 */}
                            <tr>
                                <td className="label-cell">관리지역</td>
                                <td className="input-cell">
                                    <div className="checkbox-group2">
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="지역전체"
                                                    checked={isAllSelected}
                                                    onChange={(e) => handleRegionChange('지역전체', e.target.checked)}
                                                />
                                                <span>지역전체</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="남평읍"
                                                    checked={formData.regions.includes('남평읍')}
                                                    onChange={(e) => handleRegionChange('남평읍', e.target.checked)}
                                                />
                                                <span>남평읍</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="공산면"
                                                    checked={formData.regions.includes('공산면')}
                                                    onChange={(e) => handleRegionChange('공산면', e.target.checked)}
                                                />
                                                <span>공산면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="금천면"
                                                    checked={formData.regions.includes('금천면')}
                                                    onChange={(e) => handleRegionChange('금천면', e.target.checked)}
                                                />
                                                <span>금천면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="다시면"
                                                    checked={formData.regions.includes('다시면')}
                                                    onChange={(e) => handleRegionChange('다시면', e.target.checked)}
                                                />
                                                <span>다시면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="다도면"
                                                    checked={formData.regions.includes('다도면')}
                                                    onChange={(e) => handleRegionChange('다도면', e.target.checked)}
                                                />
                                                <span>다도면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="동강면"
                                                    checked={formData.regions.includes('동강면')}
                                                    onChange={(e) => handleRegionChange('동강면', e.target.checked)}
                                                />
                                                <span>동강면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="문평면"
                                                    checked={formData.regions.includes('문평면')}
                                                    onChange={(e) => handleRegionChange('문평면', e.target.checked)}
                                                />
                                                <span>문평면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="노안면"
                                                    checked={formData.regions.includes('노안면')}
                                                    onChange={(e) => handleRegionChange('노안면', e.target.checked)}
                                                />
                                                <span>노안면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="봉황면"
                                                    checked={formData.regions.includes('봉황면')}
                                                    onChange={(e) => handleRegionChange('봉황면', e.target.checked)}
                                                />
                                                <span>봉황면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="반남면"
                                                    checked={formData.regions.includes('반남면')}
                                                    onChange={(e) => handleRegionChange('반남면', e.target.checked)}
                                                />
                                                <span>반남면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="산포면"
                                                    checked={formData.regions.includes('산포면')}
                                                    onChange={(e) => handleRegionChange('산포면', e.target.checked)}
                                                />
                                                <span>산포면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="세지면"
                                                    checked={formData.regions.includes('세지면')}
                                                    onChange={(e) => handleRegionChange('세지면', e.target.checked)}
                                                />
                                                <span>세지면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="왕곡면"
                                                    checked={formData.regions.includes('왕곡면')}
                                                    onChange={(e) => handleRegionChange('왕곡면', e.target.checked)}
                                                />
                                                <span>왕곡면</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="금남동"
                                                    checked={formData.regions.includes('금남동')}
                                                    onChange={(e) => handleRegionChange('금남동', e.target.checked)}
                                                />
                                                <span>금남동</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="빛가람동"
                                                    checked={formData.regions.includes('빛가람동')}
                                                    onChange={(e) => handleRegionChange('빛가람동', e.target.checked)}
                                                />
                                                <span>빛가람동</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="성북동"
                                                    checked={formData.regions.includes('성북동')}
                                                    onChange={(e) => handleRegionChange('성북동', e.target.checked)}
                                                />
                                                <span>성북동</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="송월동"
                                                    checked={formData.regions.includes('송월동')}
                                                    onChange={(e) => handleRegionChange('송월동', e.target.checked)}
                                                />
                                                <span>송월동</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="영강동"
                                                    checked={formData.regions.includes('영강동')}
                                                    onChange={(e) => handleRegionChange('영강동', e.target.checked)}
                                                />
                                                <span>영강동</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="영산동"
                                                    checked={formData.regions.includes('영산동')}
                                                    onChange={(e) => handleRegionChange('영산동', e.target.checked)}
                                                />
                                                <span>영산동</span>
                                            </label>
                                            <label className="checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    name="이창동"
                                                    checked={formData.regions.includes('이창동')}
                                                    onChange={(e) => handleRegionChange('이창동', e.target.checked)}
                                                />
                                                <span>이창동</span>
                                            </label>
                                    </div>
                                </td>
                            </tr>

                            {/* 기타 */}
                            <tr>
                                <td className="label-cell">기타</td>
                                <td className="input-cell">
                                    <textarea
                                        name="memo"
                                        value={formData.memo}
                                        onChange={handleChange}
                                        className="form-textarea"
                                        placeholder=""
                                    />
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        {/* 버튼 영역 */}
                        <div className="form-actions">
                            <button type="button" onClick={handleCancel} className="btn-cancel">
                                취소
                            </button>
                            <button type="submit" className="btn-submit">
                                수정하기
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AlertAdminEditView;